import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X } from 'lucide-react';

const ImageFocusModal = ({
  isOpen,
  onClose,
  onSave,
  imageSrc,
}) => {
  const [focusPoint, setFocusPoint] = useState({ x: 0.5, y: 0.5 });
  const [isDragging, setIsDragging] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const updateFocusPoint = (e) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    
    setFocusPoint({ x, y });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateFocusPoint(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      updateFocusPoint(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleSave = () => {
    onSave(focusPoint);
    onClose();
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" centered>
      <Modal.Header>
        <Modal.Title>Focus Point</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="row g-4">
          <div className="col-12 col-md-6">
            <div 
              ref={containerRef}
              className="position-relative bg-light rounded overflow-hidden cursor-crosshair"
              onMouseDown={handleMouseDown}
              style={{ aspectRatio: '1/1' }}
            >
              <img 
                ref={imageRef}
                src={imageSrc} 
                alt="Set focus point" 
                className="w-100 h-100"
                style={{ objectFit: 'contain' }}
                draggable={false}
              />
              <div 
                className="position-absolute"
                style={{
                  width: '4rem',
                  height: '4rem',
                  left: `calc(${focusPoint.x * 100}% - 2rem)`,
                  top: `calc(${focusPoint.y * 100}% - 2rem)`,
                  pointerEvents: 'none'
                }}
              >
                <div className="position-absolute w-100 h-100 border border-2 border-success rounded-circle"></div>
                <div className="position-absolute start-50 top-50 translate-middle bg-success rounded-circle" style={{ width: '4px', height: '4px' }}></div>
                <div className="position-absolute start-50 top-50 w-50 translate-middle-y bg-success" style={{ height: '1px' }}></div>
                <div className="position-absolute start-50 top-50 h-50 translate-middle-x bg-success" style={{ width: '1px' }}></div>
                <div className="position-absolute start-0 top-50 w-50 translate-middle-y bg-success" style={{ height: '1px' }}></div>
                <div className="position-absolute start-50 top-0 h-50 translate-middle-x bg-success" style={{ width: '1px' }}></div>
              </div>
            </div>
            <p className="mt-2 text-muted small">
              Click and drag to set the focus point.
            </p>
          </div>
          
          <div className="col-12 col-md-6">
            <h5>Preview your image</h5>
            <p className="text-muted small mb-4">
              See how your image looks on different screen sizes. Attendees can expand the image to see the whole thing.
            </p>
            
            <div className="d-flex flex-column gap-4">
              <div>
                <h6 className="mb-2">Square (1:1)</h6>
                <div className="bg-light rounded overflow-hidden" style={{ width: '150px', height: '150px' }}>
                  <img 
                    src={imageSrc} 
                    alt="Square preview" 
                    className="w-100 h-100"
                    style={{ 
                      objectFit: 'cover',
                      objectPosition: `${focusPoint.x * 100}% ${focusPoint.y * 100}%` 
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h6 className="mb-2">Rectangle (2:1)</h6>
                <div className="bg-light rounded overflow-hidden" style={{ width: '280px', height: '140px' }}>
                  <img 
                    src={imageSrc} 
                    alt="Rectangle preview" 
                    className="w-100 h-100"
                    style={{ 
                      objectFit: 'cover',
                      objectPosition: `${focusPoint.x * 100}% ${focusPoint.y * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageFocusModal;